/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useRef, useEffect, useMemo, useCallback, useContext } from 'react';
import { MapComponent, MapMarkerCluster, eventUtil } from '@marriott/mi-ui-library';
import { GOOGLE_MAP_VAL_KEY } from '@marriott/shared/mi-helper-utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { renderedData } from '../../organisms/OSearchResults/searchResultsMainComponent';
import { PropertyCardComponent } from '../../molecules/MPropertyCardV0';
import HotelCardContent from '../../molecules/MMapView/hotelCardContent';
import { useSearchResultsStore } from '../../modules/store/searchResultsStore';
import { useCheckBreakpoint } from '../../hooks';
import { SearchResultContext, SearchFilterContext, MapViewContext } from '../../organisms/OSearchResults/index.context';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { usePageContext } from '@marriott/mi-shop-components';
import { ChatMapViewProps, ChatMapMarker } from './ChatMapView.types';
import * as Styled from './ChatMapView.styles';
import { ChatMapMarkerStyles } from './ChatMapView.styles';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { GOOGLE_MAPS_MAP_ID } from '@marriott/mi-shop-components/constants';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { MMapPinHoverOverlay } from '../MMapPinHoverOverlay';
import { MSmartInfoWindow } from '../MSmartInfoWindow';
import { MChatMapMarker } from '../MChatMapMarker';
import { AI_SEARCH_TRACK_LABELS } from '../../constants/aiSearchTrackLabels';
import aiSparkleIconImport from '../../organisms/ONavigationSide/sparkle.png';
import { smoothFitBounds, calculateFitBoundsZoom, FitBoundsPadding } from '../../utils/helper';
import { uiContextManager } from '../../utils/uiContextManager';
import { useUIState } from '../../store';

// Handle both string and StaticImageData (Next.js) image imports
const aiSparkleIconUrl =
  typeof aiSparkleIconImport === 'string' ? aiSparkleIconImport : (aiSparkleIconImport as { src: string }).src;

// Chat cluster icon matching Figma design (56x56 with 40x40 inner circle)
// Structure: Outer transparent circle (shadow/halo) + Inner solid circle
// Default state: dark inner circle (#1c1c1c) with white text
const chatClusterIconDefault = `
<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
  <circle cx="28" cy="28" r="28" fill="black" opacity="0.1"/>
  <circle cx="28" cy="28" r="20" fill="#1c1c1c"/>
</svg>
`;

// Hover state: white inner circle with dark text
const chatClusterIconHover = `
<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
  <circle cx="28" cy="28" r="28" fill="black" opacity="0.1"/>
  <circle cx="28" cy="28" r="20" fill="#ffffff"/>
</svg>
`;

const STATIC_MAP_CENTER = { lat: 30, lng: 0 };
const STATIC_MAP_ZOOM = 2;
const MIN_ZOOM = 1;
const FIT_BOUNDS_PADDING: FitBoundsPadding = { top: 160, right: 100, bottom: 100, left: 100 };
const CLUSTER_GRID_SIZE = 20;
const MAX_CLUSTER_ZOOM = 15;
const SINGLE_MARKER_MAX_ZOOM = 14;
const MINIMUM_CLUSTER_SIZE = 5;

const dataUrl = (svg: string) => {
  const encodedSvg = btoa(svg);
  return 'data:image/svg+xml;base64,' + encodedSvg;
};

const MAP_RESTRICTION = {
  latLngBounds: { north: 85, south: -85, west: -180, east: 180 },
  strictBounds: true,
};

// Static map options - no interactions allowed
const staticMapOptions = {
  fullscreenControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  minZoom: MIN_ZOOM,
  restriction: MAP_RESTRICTION,
  zoomControl: false,
  cameraControl: false,
  gestureHandling: 'none' as const,
  mapId: GOOGLE_MAPS_MAP_ID,
  disableDefaultUI: true,
  draggable: false,
  scrollwheel: false,
  disableDoubleClickZoom: true,
  keyboardShortcuts: false,
  clickableIcons: false,
};

// Interactive map options - full interactions enabled
const interactiveMapOptions = {
  fullscreenControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  minZoom: MIN_ZOOM,
  restriction: MAP_RESTRICTION,
  zoomControl: true,
  cameraControl: false,
  gestureHandling: 'greedy' as const,
  mapId: GOOGLE_MAPS_MAP_ID,
  draggable: true,
  scrollwheel: true,
  disableDoubleClickZoom: false,
  keyboardShortcuts: true,
  clickableIcons: true,
};

const ChatMapViewComponent: React.FC<ChatMapViewProps> = ({
  markers,
  hoveredHotelId,
  onMarkerClick,
  onHotelHover: _onHotelHover,
  isStaticMap = false,
  isMapLoading = false,
  isNewSearchResult = false,
  onAnimationComplete,
  isSinglePropertyResponse = false,
}) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isAnimatingRef = useRef(false);
  const markerRefs = useRef<Map<string, google.maps.Marker>>(new Map());
  const clustererRef = useRef<any>(null);
  // Track programmatically hovered cluster for cleanup
  const hoveredClusterRef = useRef<HTMLDivElement | null>(null);
  // Track tilesloaded listener so we can clean it up
  const tilesLoadedListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  // Track if we should continue animation (becomes false when switching to static)
  const shouldAnimateRef = useRef(false);
  // Track marker IDs we've already shown to prevent unnecessary repositioning
  const animatedMarkerIdsRef = useRef<string | null>(null);
  // Track latest markers for use in async callbacks (avoids stale closure issues)
  const markersRef = useRef<ChatMapMarker[]>(markers);
  markersRef.current = markers;

  const [openInfoWindow, setOpenInfoWindow] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState<ChatMapMarker | null>(null);
  const [hoveredMarkerId, setHoveredMarkerId] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>(STATIC_MAP_CENTER);
  const selectedHotelRef = useRef<any>(null);
  const currentCenterRef = useRef<google.maps.LatLngLiteral>(STATIC_MAP_CENTER);

  // Viewport detection for responsive card rendering
  const isViewportL = useCheckBreakpoint('viewportL');
  const isViewportS = useCheckBreakpoint('viewportS');

  // Get showChat state to determine if Results tab is active on mobile
  const { showChat } = useUIState();

  // Shop contexts for HotelCardContent (mobile view)
  const { jsonContent, configData, isFlexibleDateSearch, isSpecialRateSearch, isErrorState, imageURL }: any =
    useContext(renderedData);
  const { setSelectedHotel, selectedHotel } = useContext(MapViewContext);
  const { searchResult } = useContext(SearchResultContext);
  const { searchQueryState } = useContext(SearchFilterContext);
  const { currentLocale } = usePageContext();
  const noRatesAreAvailable = useSearchResultsStore(
    (state: { noRatesAreAvailable: any }) => state.noRatesAreAvailable
  )?.noRatesAreAvailable;

  // Props for HotelCardContent (mobile view)
  const hotelCardContentArgs = useMemo(
    () => ({
      isFlexibleDateSearch: isFlexibleDateSearch ?? false,
      isSpecialRateSearch: isSpecialRateSearch ?? false,
      jsonContent: jsonContent ?? {},
      imageURL: imageURL ?? '',
      fallbackImages: { Square: jsonContent?.squareFallBackImageURL ?? '' },
      searchQueryState,
      currentLocale: currentLocale ?? 'en-US',
      setSelectedHotel: setSelectedHotel ?? (() => {}),
      configData,
      flexibleOrManyProperty: isFlexibleDateSearch ?? false,
      selectedHotel: selectedHotel ?? [],
      childCount: searchQueryState?.search?.options?.childAges?.length ?? 0,
      isViewportS,
      isViewportL,
      isErrorState: isErrorState ?? false,
      options: searchQueryState?.search?.options ?? {},
      isShowFullPrice: searchQueryState?.includeTaxesAndFees ?? false,
      selectedHotelRef,
      searchResult,
      noRatesAreAvailable,
      isSinglePropertyResponse,
    }),
    [
      isFlexibleDateSearch,
      isSpecialRateSearch,
      jsonContent,
      imageURL,
      searchQueryState,
      currentLocale,
      setSelectedHotel,
      configData,
      selectedHotel,
      isViewportS,
      isViewportL,
      isErrorState,
      searchResult,
      noRatesAreAvailable,
      isSinglePropertyResponse,
    ]
  );

  // Cleanup function for animation state
  const cleanupAnimationState = useCallback(() => {
    if (tilesLoadedListenerRef.current) {
      google.maps.event.removeListener(tilesLoadedListenerRef.current);
      tilesLoadedListenerRef.current = null;
    }
    shouldAnimateRef.current = false;
    isAnimatingRef.current = false;
  }, []);

  // Clean up animation state on unmount
  useEffect(() => {
    return () => {
      cleanupAnimationState();
    };
  }, [cleanupAnimationState]);

  // Set body overflow hidden when map view is rendered and visible
  // On desktop: always block scroll when map is mounted
  // On mobile: only block scroll when Results tab is active (showChat === false)
  // List view cases are handled by ChatMapView unmounting
  useEffect(() => {
    const shouldBlockScroll = isViewportL || !showChat;

    if (shouldBlockScroll) {
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = 'auto';
      };
    }
    return undefined;
  }, [isViewportL, showChat]);

  const handleMarkerMouseOver = useCallback((propertyId: string) => {
    setHoveredMarkerId(propertyId);
  }, []);

  const handleMarkerMouseOut = useCallback(() => {
    setHoveredMarkerId(null);
  }, []);

  // Hover lifecycle is driven by the real google.maps.Marker's mouseover/mouseout
  // (wired in MChatMapMarker). The overlay is purely visual.
  //
  // Do NOT toggle setVisible(false) on the real marker while hovered: doing so
  // creates an intermittent race on slow cursor movement where the just-restored
  // marker re-fires mouseover under the cursor and leaves the overlay stuck.

  // Default cluster styles
  const defaultClusterStyles = useMemo(
    () => [
      {
        url: dataUrl(chatClusterIconDefault),
        textColor: '#ffffff',
        width: 56,
        height: 56,
        textSize: 14,
        fontWeight: '500',
      },
    ],
    []
  );

  // Pre-compute the data URL for cluster hover icon (used by CSS)
  const clusterIconHoverUrl = useMemo(() => dataUrl(chatClusterIconHover), []);

  // Handle clusterer load - just store the ref, CSS handles hover styling
  const handleClustererLoad = useCallback((clusterer: any) => {
    clustererRef.current = clusterer;
  }, []);

  // Custom cluster click handler - calculates minimum zoom level to break cluster
  const handleClusterClick = useCallback((cluster: any) => {
    if (!mapRef.current) return;

    const clusterMarkers = cluster.getMarkers?.();
    if (!clusterMarkers || clusterMarkers.length < 2) return;

    const map = mapRef.current;

    // For large clusters (>3), use fitBounds directly without calculating minimum zoom
    if (clusterMarkers.length > 4) {
      const bounds = new google.maps.LatLngBounds();
      clusterMarkers.forEach((marker: google.maps.Marker) => {
        const pos = marker.getPosition();
        if (pos) bounds.extend(pos);
      });
      smoothFitBounds(map, bounds, FIT_BOUNDS_PADDING);
      return;
    }

    // For small clusters (≤3), continue with calculated minimum zoom

    // Get projection from clusterer (extends OverlayView) for fromLatLngToDivPixel
    const clustererProjection = clustererRef.current?.getProjection?.();
    if (!clustererProjection) {
      // Fallback: zoom in by 1 level
      const currentZoom = map.getZoom() || 10;
      map.setZoom(currentZoom + 1);
      return;
    }

    // Get screen pixel coordinates using fromLatLngToDivPixel
    const screenPoints: { x: number; y: number }[] = [];
    const bounds = new google.maps.LatLngBounds();

    clusterMarkers.forEach((marker: google.maps.Marker) => {
      const pos = marker.getPosition();
      if (pos) {
        bounds.extend(pos);
        const point = clustererProjection.fromLatLngToDivPixel(pos);
        if (point) {
          screenPoints.push({ x: point.x, y: point.y });
        }
      }
    });

    // Find MAXIMUM screen pixel distance between any two markers
    // This zooms less aggressively, breaking cluster without over-zooming
    let maxScreenDistance = 0;
    for (let i = 0; i < screenPoints.length; i++) {
      for (let j = i + 1; j < screenPoints.length; j++) {
        const dx = screenPoints[i].x - screenPoints[j].x;
        const dy = screenPoints[i].y - screenPoints[j].y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        maxScreenDistance = Math.max(maxScreenDistance, dist);
      }
    }

    const currentZoom = map.getZoom() || 10;
    const buffer = 15;
    const targetPixelDistance = CLUSTER_GRID_SIZE + buffer; // 55px

    // Calculate zoom increase needed
    // Each zoom level doubles the pixel distance
    let targetZoom = currentZoom + 1;

    if (maxScreenDistance > 0) {
      const zoomIncrease = Math.log2(targetPixelDistance / maxScreenDistance);
      targetZoom = currentZoom + zoomIncrease;

      // Enforce minimum zoom increment of 0.5
      const MIN_ZOOM_INCREMENT = 0.5;
      if (targetZoom < currentZoom + MIN_ZOOM_INCREMENT) {
        targetZoom = currentZoom + MIN_ZOOM_INCREMENT;
      }
    }

    const newZoom = targetZoom;

    // Zoom to calculated minimum level and center on markers
    map.setZoom(newZoom);
    map.setCenter(bounds.getCenter());
  }, []);

  // Reset map to world view when switching to static mode (e.g., New Chat clicked)
  useEffect(() => {
    if (!mapRef.current) return;

    // Check if map div still exists (map might be in bad state)
    const mapDiv = mapRef.current.getDiv();
    if (!mapDiv) return;

    if (isStaticMap) {
      // Clean up any pending animation state BEFORE resetting the map
      // This prevents orphaned callbacks from firing after we've reset to world view
      cleanupAnimationState();

      // Reset to world view and apply static options
      mapRef.current.setCenter(STATIC_MAP_CENTER);
      mapRef.current.setZoom(STATIC_MAP_ZOOM);
      mapRef.current.setOptions(staticMapOptions);
    } else {
      // Apply interactive options
      mapRef.current.setOptions(interactiveMapOptions);
      // Reset marker tracking so positioning runs again after exiting static mode
      // This fixes the issue where map stays at world view after chat switch
      animatedMarkerIdsRef.current = null;
    }
  }, [isStaticMap, cleanupAnimationState]);

  // Handle map positioning when markers change and map already exists
  // This handles: new search results, chat switch, subsequent searches
  // handleMapLoad handles: list-to-map switch (when map loads after markers exist)
  useEffect(() => {
    if (!mapRef.current || isStaticMap || markers.length === 0) return;

    // Prevent multiple animations
    if (isAnimatingRef.current) return;

    // Create a unique key for the current set of markers
    const markerIdsKey = markers
      .map(m => m.propertyId)
      .sort()
      .join(',');

    // Check if these are the same markers we already showed
    if (animatedMarkerIdsRef.current === markerIdsKey) {
      // Same markers, keep current view
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    markers.forEach(m => bounds.extend({ lat: m.latitude, lng: m.longitude }));

    // Mark animation as active and allow callbacks to proceed
    isAnimatingRef.current = true;
    shouldAnimateRef.current = true;

    if (isNewSearchResult) {
      // FIRST SEARCH: Animate using smoothFitBounds
      // Use 'idle' event instead of 'tilesloaded' - it's faster and more reliable
      tilesLoadedListenerRef.current = google.maps.event.addListenerOnce(mapRef.current, 'idle', () => {
        if (!shouldAnimateRef.current || !mapRef.current) {
          isAnimatingRef.current = false;
          onAnimationComplete?.();
          return;
        }

        smoothFitBounds(mapRef.current, bounds, FIT_BOUNDS_PADDING, 800, () => {
          if (markersRef.current.length === 1 && mapRef.current) {
            const currentZoom = mapRef.current.getZoom();
            if (currentZoom && currentZoom > SINGLE_MARKER_MAX_ZOOM) {
              mapRef.current.setZoom(SINGLE_MARKER_MAX_ZOOM);
            }
          }
          // Update center state to match positioned bounds
          const newCenter = bounds.getCenter();
          const centerLiteral = { lat: newCenter.lat(), lng: newCenter.lng() };
          currentCenterRef.current = centerLiteral;
          setMapCenter(centerLiteral);

          isAnimatingRef.current = false;
          shouldAnimateRef.current = false;
          animatedMarkerIdsRef.current = markerIdsKey;
          onAnimationComplete?.();
        });
      });
    } else {
      // CHAT SWITCH / SUBSEQUENT SEARCH: Use fitBounds immediately (no animation)
      // Call fitBounds immediately - it works without waiting for any event
      mapRef.current.fitBounds(bounds, FIT_BOUNDS_PADDING);

      // Use bounds_changed to know when fitBounds has repositioned the map
      // Then apply calculated zoom
      google.maps.event.addListenerOnce(mapRef.current, 'bounds_changed', () => {
        if (mapRef.current) {
          // Calculate and apply the target zoom
          const targetZoom = calculateFitBoundsZoom(mapRef.current, bounds, FIT_BOUNDS_PADDING);
          const finalZoom = markersRef.current.length === 1 ? Math.min(targetZoom, SINGLE_MARKER_MAX_ZOOM) : targetZoom;
          mapRef.current.setZoom(finalZoom);

          // Update center state to match positioned bounds
          const newCenter = bounds.getCenter();
          const centerLiteral = { lat: newCenter.lat(), lng: newCenter.lng() };
          currentCenterRef.current = centerLiteral;
          setMapCenter(centerLiteral);
        }
        isAnimatingRef.current = false;
        shouldAnimateRef.current = false;
        animatedMarkerIdsRef.current = markerIdsKey;
      });
    }
  }, [markers, isStaticMap, cleanupAnimationState, isNewSearchResult, onAnimationComplete]);

  useEffect(() => {
    if (!hoveredHotelId || !mapRef.current || isStaticMap) {
      return;
    }
    const target = markers.find(m => m.propertyId === hoveredHotelId);
    if (target) {
      mapRef.current.panTo({ lat: target.latitude, lng: target.longitude });
      setSelectedMarker(target);
      setOpenInfoWindow(true);
    }
  }, [hoveredHotelId, markers, isStaticMap]);

  // Elevate the active marker (one with InfoWindow open) above all other markers.
  // Mirrors the imperative setZIndex pattern used for hover-from-property-card so
  // the selected marker visually sits on top while the InfoWindow is shown, and
  // resets the z-index when the InfoWindow closes or the selection changes.
  useEffect(() => {
    if (!openInfoWindow || !selectedMarker) return;

    const activeMarkerInstance = markerRefs.current.get(selectedMarker.propertyId);
    if (!activeMarkerInstance) return;

    activeMarkerInstance.setZIndex(1000);

    return () => {
      // On unselect / close, drop the elevated z-index. Use the same instance
      // captured by the effect so a different marker becoming active doesn't
      // accidentally clear the new active marker's z-index.
      activeMarkerInstance.setZIndex(null);
    };
  }, [openInfoWindow, selectedMarker]);

  useEffect(() => {
    const onCardOver = (property: any) => {
      if (!property?.id) return;
      // Don't show marker / cluster hover while HQV is the active surface;
      // MMapPinHoverOverlay (z-index 1400) would otherwise paint over HQV.
      if (uiContextManager.isHqvOpen()) return;

      // Get the marker instance for this property
      const markerInstance = markerRefs.current.get(property.id);

      // Check if marker is in a cluster by iterating through clusters on-demand
      let clusterElement: HTMLDivElement | null = null;

      if (clustererRef.current && markerInstance && mapRef.current) {
        const clusters = clustererRef.current.clusters || [];
        const mapDiv = mapRef.current.getDiv();
        const clusterElements = mapDiv ? (Array.from(mapDiv.querySelectorAll('div.cluster')) as HTMLDivElement[]) : [];

        for (let i = 0; i < clusters.length; i++) {
          const cluster = clusters[i];
          const clusterMarkers = cluster.getMarkers?.() || [];

          // Check if this cluster contains our marker and meets minimum cluster size
          if (clusterMarkers.length >= MINIMUM_CLUSTER_SIZE && clusterMarkers.includes(markerInstance)) {
            clusterElement = clusterElements[i] || null;
            break;
          }
        }
      }

      if (clusterElement) {
        // Marker is in a cluster - trigger cluster hover state via CSS class
        clusterElement.classList.add('cluster-hover-active');
        hoveredClusterRef.current = clusterElement;
        setHoveredMarkerId(null); // Clear any marker hover
      } else {
        // Marker is not clustered - show individual marker hover
        const target = markers.find(m => m.propertyId === property.id);
        if (target && mapRef.current) {
          const markerPosition = new google.maps.LatLng(target.latitude, target.longitude);

          // Elevate marker z-index when hovered from property card
          if (markerInstance) {
            markerInstance.setZIndex(999);
          }

          // Check if marker is in the visible area using pixel coordinates
          const isMarkerInVisibleArea = (
            position: google.maps.LatLng,
            padding: { top?: number; right?: number; bottom?: number; left?: number }
          ) => {
            // Get projection from clusterer (extends OverlayView)
            const projection = clustererRef.current?.getProjection?.();
            if (!projection || !mapRef.current) return false;

            // Convert lat/lng to container pixel coordinates
            const pixel = projection.fromLatLngToContainerPixel(position);
            if (!pixel) return false;

            const mapDiv = mapRef.current.getDiv();
            const mapWidth = mapDiv.offsetWidth;
            const mapHeight = mapDiv.offsetHeight;

            // Check if pixel is within visible area (accounting for padding)
            const paddingTop = padding.top ?? 0;
            const paddingRight = padding.right ?? 0;
            const paddingBottom = padding.bottom ?? 0;
            const paddingLeft = padding.left ?? 0;

            return (
              pixel.x >= paddingLeft &&
              pixel.x <= mapWidth - paddingRight &&
              pixel.y >= paddingTop &&
              pixel.y <= mapHeight - paddingBottom
            );
          };

          // If marker is outside the visible area, pan to it and wait for pan to complete
          if (!isMarkerInVisibleArea(markerPosition, FIT_BOUNDS_PADDING)) {
            mapRef.current.panTo(markerPosition);
            // Wait for idle event (pan animation complete) before showing overlay
            google.maps.event.addListenerOnce(mapRef.current, 'idle', () => {
              setHoveredMarkerId(target.propertyId);
            });
          } else {
            // Marker is fully visible, show overlay immediately
            setHoveredMarkerId(target.propertyId);
          }
        }
      }
    };

    const onCardOut = () => {
      // Reset cluster hover if active
      if (hoveredClusterRef.current) {
        hoveredClusterRef.current.classList.remove('cluster-hover-active');
        hoveredClusterRef.current = null;
      }

      // Reset z-index for all markers that might have been hovered
      markerRefs.current.forEach(markerInstance => {
        markerInstance.setZIndex(null);
      });

      setHoveredMarkerId(null);
    };

    eventUtil.on('propCardOver', onCardOver);
    eventUtil.on('propCardUnOver', onCardOut);
    return () => {
      eventUtil.remove('propCardOver', onCardOver as unknown as () => void);
      eventUtil.remove('propCardUnOver', onCardOut as unknown as () => void);
    };
  }, [markers]);

  useEffect(() => {
    if (!isStaticMap || !mapRef.current) return;
    const container = mapRef.current.getDiv();
    if (!container) return;
    const observer = new ResizeObserver(() => {
      google.maps.event.trigger(mapRef.current!, 'resize');
      mapRef.current!.setCenter(STATIC_MAP_CENTER);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [isStaticMap]);

  // Close InfoWindow when clicking outside the map container
  useEffect(() => {
    if (!openInfoWindow) return;

    const handleClickOutside = (event: MouseEvent) => {
      const mapContainer = mapRef.current?.getDiv();
      const target = event.target as Node;

      // Check if click is inside the InfoWindow content (has data-info-window attribute or is a child of it)
      const infoWindowContent = document.querySelector('[data-info-window-content]');
      if (infoWindowContent?.contains(target)) {
        return;
      }

      if (mapContainer && !mapContainer.contains(target)) {
        setOpenInfoWindow(false);
        setSelectedMarker(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openInfoWindow]);

  const handleMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;

      // Handle list-to-map switch: position map if markers exist but haven't been positioned yet
      // Guards ensure this only runs when useEffect couldn't (map wasn't ready when useEffect ran)
      // Note: List-to-map switch is never a "new search", so we always use instant positioning

      // Guard 1: Only proceed if we have markers to show
      if (markers.length === 0) return;

      // Guard 2: Don't interfere if animation is already in progress
      if (isAnimatingRef.current) return;

      // Guard 3: Don't reposition if we've already shown these exact markers
      const markerIdsKey = markers
        .map(m => m.propertyId)
        .sort()
        .join(',');
      if (animatedMarkerIdsRef.current === markerIdsKey) return;

      // Guard 4: Don't run if map is in static mode
      if (isStaticMap) return;

      // If we reach here: map just loaded with unpositioned markers (list-to-map switch)
      // Use instant positioning - call fitBounds immediately
      const bounds = new google.maps.LatLngBounds();
      markers.forEach(m => bounds.extend({ lat: m.latitude, lng: m.longitude }));

      isAnimatingRef.current = true;
      shouldAnimateRef.current = true;

      // Call fitBounds immediately - it works without waiting for any event
      map.fitBounds(bounds, FIT_BOUNDS_PADDING);

      // Use bounds_changed to know when fitBounds has repositioned the map
      // Then apply calculated zoom
      google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
        if (mapRef.current) {
          // Calculate and apply the target zoom
          const targetZoom = calculateFitBoundsZoom(mapRef.current, bounds, FIT_BOUNDS_PADDING);
          const finalZoom = markersRef.current.length === 1 ? Math.min(targetZoom, SINGLE_MARKER_MAX_ZOOM) : targetZoom;
          mapRef.current.setZoom(finalZoom);

          // Update center state to match positioned bounds
          const newCenter = bounds.getCenter();
          const centerLiteral = { lat: newCenter.lat(), lng: newCenter.lng() };
          currentCenterRef.current = centerLiteral;
          setMapCenter(centerLiteral);
        }
        isAnimatingRef.current = false;
        shouldAnimateRef.current = false;
        animatedMarkerIdsRef.current = markerIdsKey;
      });
    },
    [markers, isStaticMap]
  );

  const handleMapClick = useCallback(() => {
    setOpenInfoWindow(false);
    setSelectedMarker(null);
  }, []);

  const handleMarkerClick = useCallback((marker: ChatMapMarker) => {
    setSelectedMarker(marker);
    setOpenInfoWindow(true);
    // Only show property card on marker click, don't open HQV
    // HQV will be opened when user clicks on the property card itself
  }, []);

  // Handler for property card click - opens HQV
  const handlePropertyCardClick = useCallback(
    (propertyData: any, propertyId: string, options?: { openGallery?: boolean }) => {
      // Close the InfoWindow before HQV opens so it doesn't paint on top of HQV.
      setOpenInfoWindow(false);
      setSelectedMarker(null);
      onMarkerClick(propertyData, propertyId, options);
    },
    [onMarkerClick]
  );

  const handleInfoWindowClose = useCallback(() => {
    setOpenInfoWindow(false);
    setSelectedMarker(null);
  }, []);

  const mapContainerStyle = useMemo(
    () => ({
      position: 'static' as const,
      overflow: 'visible' as const,
      height: '100%',
      width: '100%',
    }),
    []
  );

  // Use stable initial zoom - position changes are handled via mapRef and fitBounds
  // Center is managed via mapCenter state to prevent GoogleMap from resetting position
  const initialZoom = STATIC_MAP_ZOOM;

  // Select map options based on static/interactive mode
  const mapOptions = isStaticMap ? staticMapOptions : interactiveMapOptions;
  const mapApiKey =
    useClientEnvVarsStore(state => state.envVarsObject?.['GOOGLE_MAP_API_KEY']) || (GOOGLE_MAP_VAL_KEY as string);

  return (
    <Styled.ChatMapContainer ref={containerRef}>
      <ChatMapMarkerStyles $clusterHoverIconUrl={clusterIconHoverUrl} $aiSparkleIconUrl={aiSparkleIconUrl} />
      <MapComponent
        googleMapsApiKey={mapApiKey}
        mapId={GOOGLE_MAPS_MAP_ID}
        mapHeight="100%"
        center={mapCenter}
        zoom={initialZoom}
        options={mapOptions}
        onLoad={handleMapLoad}
        onClick={handleMapClick}
        onDragStart={handleMapClick}
        mapContainerStyle={mapContainerStyle}
      >
        {!isStaticMap && markers.length > 0 && (
          <MapMarkerCluster
            averageCenter={false}
            gridSize={CLUSTER_GRID_SIZE}
            minimumClusterSize={MINIMUM_CLUSTER_SIZE}
            maxZoom={MAX_CLUSTER_ZOOM}
            onLoad={handleClustererLoad}
            onClick={handleClusterClick}
            onClusteringEnd={() => {
              // Clear hover state if the hovered marker is now in a cluster
              if (hoveredMarkerId) {
                const markerInstance = markerRefs.current.get(hoveredMarkerId);
                if (markerInstance && markerInstance.getMap() === null) {
                  setHoveredMarkerId(null);
                }
              }
            }}
            options={{
              imagePath: '',
              styles: defaultClusterStyles,
            }}
          >
            {(clusterer: any) => (
              <>
                {markers.map(marker => {
                  const isActive = selectedMarker?.propertyId === marker.propertyId;
                  const isHovered = hoveredMarkerId === marker.propertyId;

                  return (
                    <MChatMapMarker
                      key={marker.propertyId}
                      marker={marker}
                      clusterer={clusterer}
                      isActive={isActive}
                      isHovered={isHovered}
                      onMarkerClick={handleMarkerClick}
                      onMarkerMouseOver={handleMarkerMouseOver}
                      onMarkerMouseOut={handleMarkerMouseOut}
                      markerRefs={markerRefs}
                      isSinglePropertyResponse={isSinglePropertyResponse}
                    />
                  );
                })}
                {openInfoWindow && selectedMarker && isViewportL && mapRef.current && (
                  <MSmartInfoWindow
                    marker={selectedMarker}
                    map={mapRef.current}
                    onClose={handleInfoWindowClose}
                    portalContainer={containerRef.current}
                    isSinglePropertyResponse={isSinglePropertyResponse}
                  >
                    <Styled.MapInfoWindowContent
                      data-info-window-content
                      data-track-label={AI_SEARCH_TRACK_LABELS.MAP_PROPERTY_CARD}
                      onClick={e => e.stopPropagation()}
                      onDoubleClick={e => e.stopPropagation()}
                    >
                      <PropertyCardComponent
                        propertyCardData={selectedMarker.edge}
                        idx={0}
                        onOpenHotelPanel={(property, id, options) => {
                          handlePropertyCardClick(property ?? selectedMarker, id, options);
                        }}
                        isSinglePropertyResponse={isSinglePropertyResponse}
                      />
                    </Styled.MapInfoWindowContent>
                  </MSmartInfoWindow>
                )}
              </>
            )}
          </MapMarkerCluster>
        )}
      </MapComponent>

      {/* Portaled hover overlay - sits above clusters, filter chips, and the
          mobile list toggle. Purely visual; the real google.maps.Marker beneath
          owns hover/click. */}
      {hoveredMarkerId &&
        !openInfoWindow &&
        mapRef.current &&
        (() => {
          const hoveredMarker = markers.find(m => m.propertyId === hoveredMarkerId);
          if (!hoveredMarker) return null;

          return (
            <MMapPinHoverOverlay
              marker={hoveredMarker}
              map={mapRef.current!}
              portalContainer={containerRef.current}
              isSinglePropertyResponse={isSinglePropertyResponse}
            />
          );
        })()}

      {isMapLoading && (
        <Styled.DotsLoaderOverlay>
          <Styled.DotsLoaderPill>
            <Styled.DotsLoaderDot />
            <Styled.DotsLoaderDot $delay="0.2s" />
            <Styled.DotsLoaderDot $delay="0.4s" />
          </Styled.DotsLoaderPill>
        </Styled.DotsLoaderOverlay>
      )}

      {/* Mobile bottom sheet card - fixed at bottom of screen */}
      {openInfoWindow && selectedMarker && !isViewportL && (
        <Styled.MobileBottomCard
          data-track-label={AI_SEARCH_TRACK_LABELS.MAP_PROPERTY_CARD}
        >
          <HotelCardContent
            item={selectedMarker.edge}
            index={0}
            cardIndex={0}
            args={hotelCardContentArgs}
            onOpenHotelPanel={(property: any, id: string, options?: { openGallery?: boolean }) => {
              handlePropertyCardClick(property ?? selectedMarker, id, options);
            }}
          />
        </Styled.MobileBottomCard>
      )}
      {/* TODO(future:analytics): Track map interaction events (zoom, pan, marker clicks) */}
    </Styled.ChatMapContainer>
  );
};

export const ChatMapView = React.memo(ChatMapViewComponent);
